import React, { useEffect, useState } from "react";
import { BlockStack, Text, TextField, Select, Checkbox, Link } from "@shopify/polaris";
import { symbolFor } from "./BarWidget";
import EnableFreeShippingHelpModal from "./EnableFreeShippingHelpModal";

export default function ContentSection({
  name,
  content,
  design,
  currencyOptions = [],
  currencyLoading = false,
  primaryCurrency = "USD",
  onPatchName,
  onPatch,
  onPatchDesign,
}) {
  const c = content || {};
  const goal = c.goal || {};
  const messages = c.messages || {};
  const showProgressBar = design?.progress_bar?.visible !== false;
  const showMessage = design?.typography?.message?.visible !== false;

  const selectedCurrency = goal.currency || primaryCurrency || "USD";
  const currencyPrefix = symbolFor(selectedCurrency);
  const [helpOpen, setHelpOpen] = useState(false);
  const goalAmount = Number(goal.amount ?? 50);

  useEffect(() => {
    if (currencyLoading || !currencyOptions.length) return;
    const valid = currencyOptions.some((o) => o.value === goal.currency);
    if (!valid) {
      const next = currencyOptions.find((o) => o.primary)?.value || currencyOptions[0].value;
      onPatch({ goal: { currency: next } });
    }
  }, [currencyLoading, currencyOptions, goal.currency, onPatch]);

  const selectOptions =
    currencyOptions.length > 0
      ? currencyOptions
      : [{ label: "Loading…", value: selectedCurrency, disabled: true }];

  return (
    <BlockStack gap="400">
      <TextField
        label="Bar name"
        helpText="Only visible to you, for your own internal reference."
        value={name || ""}
        onChange={onPatchName}
        autoComplete="off"
      />

      <Text as="h3" variant="headingSm">
        Goal
      </Text>
      <TextField
        label="Goal"
        labelHidden
        type="number"
        prefix={currencyPrefix}
        value={String(goal.amount ?? 50)}
        onChange={(v) => onPatch({ goal: { ...goal, amount: Number(v) || 0 } })}
        autoComplete="off"
        helpText={
          <span>
            This app displays a progress bar only — it does not apply checkout discounts.{" "}
            <Link monochrome onClick={() => setHelpOpen(true)}>
              How to enable free shipping in Shopify settings
            </Link>
          </span>
        }
      />
      <EnableFreeShippingHelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        goalAmount={goalAmount}
        currency={selectedCurrency}
      />
      <Select
        label="Currency"
        options={selectOptions}
        value={selectedCurrency}
        onChange={(v) => onPatch({ goal: { ...goal, currency: v } })}
        disabled={currencyLoading}
        helpText={
          currencyLoading
            ? "Loading currencies from your store markets…"
            : currencyOptions.length === 1
              ? "Based on your store's market setup."
              : "Currencies from your Shopify Markets."
        }
      />

      <TextField
        label="Message"
        helpText="Use {order-value} to display the order value goal."
        value={messages.initial || ""}
        onChange={(v) => onPatch({ messages: { ...messages, initial: v } })}
        autoComplete="off"
      />
      <TextField
        label="Progress message"
        helpText="Use {order-value-progress} to display the remaining amount."
        value={messages.progress || ""}
        onChange={(v) => onPatch({ messages: { ...messages, progress: v } })}
        autoComplete="off"
      />
      <TextField
        label="Goal reached message"
        value={messages.success || ""}
        onChange={(v) => onPatch({ messages: { ...messages, success: v } })}
        autoComplete="off"
      />

      <Checkbox
        label="Show message"
        checked={showMessage}
        onChange={(v) =>
          onPatchDesign({ typography: { message: { ...(design?.typography?.message || {}), visible: v } } })
        }
        helpText="Turn off to hide the shipping message text on the storefront."
      />

      <Checkbox
        label="Show progress bar"
        checked={showProgressBar}
        onChange={(v) => onPatchDesign({ progress_bar: { visible: v } })}
        helpText="Turn off to display only the message text without a progress bar."
      />
    </BlockStack>
  );
}
